/**
 * @file CrossfitDebutant.tsx
 * @summary Page service
 *
 * Page descriptive d'un service proposé par la box (cours WOD, débutant, hybride, reprise du sport).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import ServicePageLayout from "@/components/ServicePageLayout";
import { Link } from "react-router-dom";

const URL = "https://crossfitchistera.fr/crossfit-debutant";

const faq = [
  {
    "@type": "Question",
    name: "Peut-on commencer le CrossFit sans aucune expérience ?",
    acceptedAnswer: { "@type": "Answer", text: "Oui. La majorité des nouveaux adhérents de CrossFit Chistera sont des débutants. Les 2 premières séances sont gratuites et adaptées à votre niveau. Le coach corrige les mouvements en direct." },
  },
  {
    "@type": "Question",
    name: "Le CrossFit est-il dangereux pour un débutant ?",
    acceptedAnswer: { "@type": "Answer", text: "Non, à condition d'être encadré. Chez Chistera, les cours sont limités à 12 personnes et les coachs (Master STAPS, BPJEPS, CrossFit Level 1) corrigent chaque mouvement. Les charges démarrent légères et progressent avec la technique." },
  },
  {
    "@type": "Question",
    name: "Faut-il du matériel pour débuter ?",
    acceptedAnswer: { "@type": "Answer", text: "Non. Toute l'équipement (barres, haltères, kettlebells, anneaux, rameurs, ski erg, etc.) est fourni. Tenue de sport et chaussures plates suffisent." },
  },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Initiation CrossFit pour débutant",
    provider: { "@id": "https://crossfitchistera.fr/#localbusiness" },
    url: URL,
    description: "Démarrer le CrossFit en toute sécurité à Saint-Pierre-de-Chandieu : 2 séances d'essai gratuites, cours scalés, coaching diplômé.",
  },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
];

export default function CrossfitDebutant() {
  return (
    <>
      <SEO
        title="CrossFit débutant Lyon Est — Séance gratuite — Chistera"
        description="Démarrer le CrossFit à Saint-Pierre-de-Chandieu : 2 séances d'essai gratuites, cours scalés à votre niveau, coachs diplômés. Sport santé, communauté."
        canonical={URL}
        jsonLd={jsonLd}
      />
      <ServicePageLayout
        breadcrumbs={[{ label: "CrossFit débutant" }]}
        h1="Démarrer le CrossFit quand on est débutant"
        intro="On ne devient pas CrossFitter en un jour : on commence. Chez CrossFit Chistera, la majorité des adhérents sont arrivés débutants. Cours scalés à votre niveau, coachs diplômés, 12 personnes maximum, et 2 séances d'essai gratuites pour vous lancer sans pression."
      >
        <h2>Le CrossFit, ce n'est pas réservé aux athlètes</h2>
        <p>
          Une idée reçue tenace : il faudrait « être en forme » avant de commencer le CrossFit. C'est faux. Le CrossFit est une <strong>méthode complète accessible à tous</strong> — adolescent, parent débordé, senior, personne en surpoids, sportif confirmé. Le secret : le <em>scaling</em>, c'est-à-dire l'adaptation des charges, des mouvements et du volume à votre niveau réel.
        </p>

        <h2>Vos premières séances chez Chistera</h2>
        <ol>
          <li><strong>Vous réservez vos 2 séances d'essai gratuites</strong> via le formulaire de contact.</li>
          <li><strong>Vous arrivez en tenue de sport</strong> (chaussures plates type running ou cross-training, bouteille d'eau).</li>
          <li><strong>Le coach vous accueille</strong>, vous présente la box, le matériel et le déroulé du cours.</li>
          <li><strong>Vous suivez le cours collectif</strong> avec des charges et des variantes adaptées. Le coach corrige chaque mouvement.</li>
          <li><strong>On débriefe</strong> à la fin pour discuter de vos sensations, de vos objectifs et des cours qui pourraient vous convenir.</li>
        </ol>

        <h2>Ce que vous allez ressentir</h2>
        <ul>
          <li>Du <strong>plaisir</strong> à bouger en groupe, dans une ambiance bienveillante.</li>
          <li>Une <strong>fatigue saine</strong> en sortant — c'est normal, ça passera vite avec la régularité.</li>
          <li>Des <strong>courbatures</strong> dans les jours qui suivent, signe que les muscles ont travaillé.</li>
          <li>Une <strong>progression rapide</strong> : la majorité des débutants notent une amélioration en 4-6 semaines (tonus, sommeil, énergie).</li>
        </ul>

        <h2>Quels cours pour débuter ?</h2>
        <p>
          Tous nos cours sont accessibles aux débutants : <Link to="/cours/wod">le cours WOD CrossFit</Link> est l'entrée naturelle. Si vous reprenez le sport après une longue pause, lisez aussi notre page <Link to="/reprendre-le-sport">reprendre le sport</Link>. Pour le cardio long et la prépa Hyrox, voir <Link to="/cours/hybrid-training">Hybrid Training</Link>.
        </p>

        <h2>Encadrement</h2>
        <p>
          Tous les cours sont assurés par <strong>Anthony Bendahmane</strong> (Master STAPS, CrossFit Level 1) et <strong>Cédric Bastien</strong> (BPJEPS, CrossFit Level 1), deux coachs diplômés et anciens rugbymen, habitués à accompagner des publics variés (débutants, sportifs en reprise, seniors, enfants).
        </p>

        <h2>Tarifs débutant</h2>
        <p>
          <strong>2 séances d'essai gratuites</strong>. Ensuite, abonnements à partir de 64 €/mois (2 séances/sem). Frais d'inscription 50 €.
        </p>

        <h2>Questions fréquentes</h2>
        <h3>Sans aucune expérience sportive ?</h3>
        <p>Aucun problème. C'est même le profil le plus fréquent.</p>
        <h3>Est-ce dangereux ?</h3>
        <p>Pas avec un encadrement diplômé et des cours à 12 max comme chez Chistera.</p>
        <h3>Quel matériel apporter ?</h3>
        <p>Une tenue de sport et des chaussures plates. Le reste est fourni.</p>
      </ServicePageLayout>
    </>
  );
}
